import { useCallback, useEffect, useRef, useState } from 'react';

import { useJobCompletion } from 'lib/hooks/useJobCompletion';
import { api } from 'lib/request/api';
import type {
  DownloadResponse,
  DownloadTrackerResponse,
} from 'types/api-responses';

// The backstop for a push that never arrives, or never comes because the job
// failed (a download pushes only once processed). Comparable downloads on the
// same queue are ready in about 1-3 s at the median and 3-10 s at the 90th
// percentile, so the first read comes 2 s after the answer and one every 3 s
// after: a missed push costs a small export a few seconds, and a read is one
// row by id. Past the cap the wait fails, for a job lost mid-build (e.g.
// requeued on a worker restart) never reaches an end of its own.
export const BACKGROUND_DOWNLOAD_POLL = {
  delayMs: 2_000,
  intervalMs: 3_000,
  maxMs: 15 * 60_000,
};

/**
 * What to tell the user once an export has ended: its file is ready to
 * download, or it failed (the request, the job or the wait), keeping the href
 * and body it sent so Try again repeats it.
 */
type BackgroundDownloadNotice =
  | { kind: 'ready'; fileUrl: string }
  | { kind: 'failed'; href: string; body: Record<string, unknown> };

/**
 * Runs an export built in a background download: posts the body to an
 * endpoint that answers with the shared `/downloads` tracker, and waits for
 * the download. The push is only a hint: the download is re-read on
 * subscribing, on the push and on a poll, so a missed push, a failed job or a
 * lost one still ends the wait. Nothing opens on its own: the ready file waits
 * for the user's click, which pop-up blockers let through. Call it where it
 * outlives the screens that start an export, so the export stays busy and its
 * message stays up whichever one the user is on. Unmounting drops the export,
 * and its promise still ends. Unlike useDownloadResource (useDownloadFile.ts),
 * which opens the file itself on the push, this re-reads the download, caps
 * the wait and leaves opening the file to the user.
 * @return {object} `building` from the click until the export has ended;
 *   `runExport`, which posts this body to this href (while one export builds,
 *   it returns that export's promise and sends nothing); `notice`, the message
 *   of the last export until the user acts on it or starts another; and
 *   `dismissNotice`.
 */
export const useBackgroundDownload = () => {
  const [pending, setPending] = useState<{
    tracker: DownloadTrackerResponse;
    settle: (download: DownloadResponse | null) => void;
  } | null>(null);
  const [building, setBuilding] = useState(false);
  const [notice, setNotice] = useState<BackgroundDownloadNotice | null>(null);
  const inFlight = useRef<Promise<void> | null>(null);
  const mounted = useRef(false);

  useJobCompletion({
    channel: pending?.tracker.notifications.channel,
    events: pending?.tracker.notifications.events.map(({ name }) => name),
    reconcile: async () => {
      if (!pending) return;
      // A failed read throws, and decides nothing: the next trigger reads again.
      const download = await api
        .get(pending.tracker.href.replace(/^\//, ''))
        .json<DownloadResponse>();
      // A promise settles once, so a second trigger reading the same end is a no-op.
      if (download.status === 'processed' || download.status === 'failed') {
        pending.settle(download);
      }
    },
    poll: pending
      ? { ...BACKGROUND_DOWNLOAD_POLL, onExpired: () => pending.settle(null) }
      : null,
  });

  // Unmounting ends the wait, so the export's promise settles. Also runs when
  // a wait ends, on a promise already settled: a no-op.
  useEffect(() => () => pending?.settle(null), [pending]);

  // An export answered after unmounting has nothing to wait in, so it ends at once.
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // A new export clears the last one's message; this one ends with its own.
  const send = useCallback(
    async (href: string, body: Record<string, unknown>) => {
      setNotice(null);
      setBuilding(true);
      try {
        const tracker = await api
          .post(href, { json: body })
          .json<DownloadTrackerResponse>();
        const download = await new Promise<DownloadResponse | null>(
          (settle) => {
            if (mounted.current) setPending({ tracker, settle });
            else settle(null);
          }
        );
        setPending(null);
        if (download?.status !== 'processed') {
          throw new Error('Export failed');
        }
        setNotice({ kind: 'ready', fileUrl: download.fileUrl });
      } catch {
        setNotice({ kind: 'failed', href, body });
      } finally {
        setBuilding(false);
      }
    },
    []
  );

  const runExport = useCallback(
    (href: string, body: Record<string, unknown>) => {
      inFlight.current ??= send(href, body).finally(() => {
        inFlight.current = null;
      });
      return inFlight.current;
    },
    [send]
  );

  const dismissNotice = useCallback(() => setNotice(null), []);

  return { building, runExport, notice, dismissNotice };
};
